import React, { useEffect, useState } from "react";
import {
  Tooltip,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { useThumbzApi } from "src/services/thumbz-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "src/config/query-key";
import { IApprovalFlow } from "src/services/thumbz-base-api";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";
import { Add, Visibility, Delete, MoreVert } from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import { useRouter } from "src/hooks/use-router";
import { ApprovalFlowUpsertForm } from "src/sections/approval-flow/approval-flow-upsert-form";

interface ApprovalFlowTableProps {
  wrk_id: string;
}

export const ApprovalFlowTable: React.FC<ApprovalFlowTableProps> = ({ wrk_id }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Estado para controlar o diálogo
  const router = useRouter();
  const confirm = useConfirm();
  const [rowData, setRowData] = useState<IApprovalFlow[]>([]);
  const queryClient = useQueryClient();
  const { api } = useThumbzApi();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuFlow, setMenuFlow] = useState<IApprovalFlow | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, flow: IApprovalFlow) => {
    setMenuAnchor(event.currentTarget);
    setMenuFlow(flow);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuFlow(null);
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleAddFlow = (flowName: string, flowDescription: string) => {
    createApprovalFlowMutation.mutate({
      flow_name: flowName,
      flow_description: flowDescription,
      wrk_id,
    });
  };

  function clearCache() {
    queryClient.invalidateQueries({
      queryKey: queryKeys.approvalFlow.approvalFlowControllerList({ wrk_id }).queryKey,
    });
  }

  const approvalFlowsQuery = useQuery({
    queryKey: queryKeys.approvalFlow.approvalFlowControllerList({ wrk_id }).queryKey,
    queryFn: () => api.approvalFlow.approvalFlowControllerList({ wrk_id }),
    enabled: !!wrk_id,
  });

  const createApprovalFlowMutation = useMutation({
    mutationFn: api.approvalFlow.approvalFlowControllerUpsert,
    onSuccess: ({ flow_id }) => {
      clearCache();
      toast.success("Fluxo de aprovação criado com sucesso");
      router.navigate({
        from: "/workspaces/$wrk_id",
        to: "/workspaces/$wrk_id/flow/$flow_id",
        params: { wrk_id, flow_id },
      });
    },
    onError: (error) =>
      getServerErrorArrayMessage(error).forEach((message) => toast.error(message)),
  });

  const deleteApprovalFlowMutation = useMutation({
    mutationFn: api.approvalFlow.approvalFlowControllerDelete,
    onSuccess: () => {
      clearCache();
      toast.success("Fluxo de aprovação deletado com sucesso");
    },
    onError: (error) =>
      getServerErrorArrayMessage(error).forEach((message) => toast.error(message)),
  });

  useEffect(() => {
    if (approvalFlowsQuery.data) {
      setRowData(approvalFlowsQuery.data);
    }
  }, [approvalFlowsQuery.data]);

  const handleDelete = (flow_id: string) => {
    confirm({
      description: "Tem certeza que deseja excluir o fluxo de aprovação?",
      confirmationText: "Sim, excluir",
      confirmationButtonProps: {
        color: "error",
      },
    }).then(() => {
      deleteApprovalFlowMutation.mutate({ flow_id });
    });
  };

  function handleViewSelectedFlow(flow: IApprovalFlow): void {
    router.navigate({
      to: "/workspaces/$wrk_id/flow/$flow_id",
      params: { wrk_id, flow_id: flow.flow_id },
    });
  }

  return (
    <Stack spacing={2}>
      <Stack justifyContent="flex-end" flex={1} direction="row">
        <Tooltip title="Novo Modelo">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleDialogOpen} // Abre o diálogo ao clicar
            startIcon={<Add />}
          >
            Novo Modelo
          </Button>
        </Tooltip>
      </Stack>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="medium" aria-label="Modelos de aprovação">
          <TableHead>
            <TableRow>
              <TableCell scope="col">Nome</TableCell>
              <TableCell scope="col">Descrição</TableCell>
              <TableCell scope="col">Etapas</TableCell>
              <TableCell align="right" scope="col">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowData.map((flow) => (
              <TableRow hover key={flow.flow_id}>
                <TableCell>{flow.flow_name}</TableCell>
                <TableCell>{flow.flow_description || "Sem descrição"}</TableCell>
                <TableCell>
                  {flow.stages?.map((s, i) => `${i + 1}. ${s.afs_name}`).join(", ") || "Sem etapas"}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={(e) => handleMenuOpen(e, flow)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (menuFlow) handleViewSelectedFlow(menuFlow);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Visualizar</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuFlow) handleDelete(menuFlow.flow_id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      <ApprovalFlowUpsertForm
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleAddFlow}
      />
    </Stack>
  );
};
